import { z } from 'zod';

export const DEPOSIT_STATUSES = ['outstanding', 'cleared', 'voided'] as const;
export type DepositStatus = (typeof DEPOSIT_STATUSES)[number];

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const positiveMoney = z
  .string()
  .regex(/^\d+(\.\d{1,4})?$/, 'must be a positive money value')
  .refine((v) => Number(v) > 0, 'must be greater than zero');

export const depositLineInputSchema = z.object({
  accountId: z.string().uuid(),
  amount: positiveMoney,
  memo: z.string().max(500).nullable().optional(),
  contactId: z.string().uuid().nullable().optional(),
  locationId: z.string().uuid().nullable().optional(),
  classId: z.string().uuid().nullable().optional(),
});
export type DepositLineInput = z.infer<typeof depositLineInputSchema>;

export const recordDepositInputSchema = z.object({
  bankAccountId: z.string().uuid(), // GL bank account the funds go into (must be a bank)
  depositDate: isoDate,
  reference: z.string().max(50).nullable().optional(),
  // The payer. Optional for a batch deposit covering several customers; each line
  // can name its own customer instead.
  contactId: z.string().uuid().nullable().optional(),
  memo: z.string().max(200).nullable().optional(),
  lines: z.array(depositLineInputSchema).min(1),
  /** Set after the user confirms a possible-duplicate warning. */
  allowDuplicate: z.boolean().optional(),
});
export type RecordDepositInput = z.infer<typeof recordDepositInputSchema>;

export const voidDepositInputSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type VoidDepositInput = z.infer<typeof voidDepositInputSchema>;

export const listDepositsQuerySchema = z.object({
  bankAccountId: z.string().uuid().optional(),
  status: z.enum(DEPOSIT_STATUSES).optional(),
});
export type ListDepositsQuery = z.infer<typeof listDepositsQuerySchema>;

export interface Deposit {
  id: string;
  companyId: string;
  bankAccountId: string;
  depositDate: string;
  reference: string | null;
  contactId: string | null;
  payerName: string;
  amount: string;
  memo: string | null;
  status: DepositStatus;
  journalEntryId: string | null;
  clearedAt: string | null;
  voidReason: string | null;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DepositLine {
  accountId: string;
  accountCode: string;
  accountName: string;
  amount: string;
  memo: string | null;
}

export interface DepositWithLines extends Deposit {
  bankAccountCode: string;
  bankAccountName: string;
  lines: DepositLine[];
}
